import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Phone, ArrowLeft, FileText, CheckCircle } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import SectionFadeIn from "@/components/SectionFadeIn";

const ConstructionContractsGuide = () => {
  const { t, i18n } = useTranslation('resources');
  const prefix = i18n.language === 'es' ? '/es' : '';
  const k = 'constructionContracts';

  const sections = t(`${k}.sections`, { returnObjects: true }) as Array<{
    heading: string;
    level?: number;
    content: string[];
    list?: string[];
    contentAfterList?: string[];
  }>;

  const contractTypes = t(`${k}.contractTypes`, { returnObjects: true }) as Array<{
    code: string;
    name: string;
    bestFor: string;
    description: string;
  }>;

  const faq = t(`${k}.faq`, { returnObjects: true }) as Array<{ question: string; answer: string }>;
  const sidebarLinks = t(`${k}.sidebar.related.links`, { returnObjects: true }) as string[];

  return (
    <Layout>
      <SEO
        title={t(`${k}.seo.title`)}
        description={t(`${k}.seo.description`)}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Construction Contracts Guide for NYC Contractors: Which Contract Do You Need?",
            "author": { "@type": "Organization", "name": "ARCONDES" },
            "publisher": { "@type": "Organization", "name": "ARCONDES", "url": "https://www.arcondes.com" },
            "datePublished": "2026-04-05",
            "articleSection": "Education",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faq.map((f) => ({
              "@type": "Question",
              "name": f.question,
              "acceptedAnswer": { "@type": "Answer", "text": f.answer },
            })),
          },
        ]}
      />

      {/* Hero */}
      <section className="relative py-16 sm:py-24 bg-secondary">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <SectionFadeIn>
            <div className="max-w-3xl">
              <Link to={`${prefix}/resources`} className="inline-flex items-center gap-2 text-muted-foreground text-sm hover:text-accent mb-6 transition-colors">
                <ArrowLeft className="h-4 w-4" />{t(`${k}.backLink`)}
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-accent">{t(`${k}.category`)}</span>
                <span className="text-xs text-muted-foreground">{t(`${k}.readTime`)}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
                {t(`${k}.title`)}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t(`${k}.subtitle`)}
              </p>
            </div>
          </SectionFadeIn>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2">
              <article>
                <SectionFadeIn>
                  <div className="space-y-6 text-muted-foreground leading-relaxed">
                    {/* Main sections */}
                    {sections.map((section, sIdx) => {
                      const isFirst = sIdx === 0;
                      const HeadingTag = section.level === 3 ? 'h3' : 'h2';
                      const headingClass = section.level === 3
                        ? 'text-xl font-bold text-foreground'
                        : `text-2xl font-bold text-foreground ${isFirst ? 'mt-0' : ''}`;

                      return (
                        <div key={sIdx}>
                          <HeadingTag className={headingClass}>{section.heading}</HeadingTag>
                          {section.content.map((p, pIdx) => (
                            <p key={pIdx} className="mt-4">{p}</p>
                          ))}
                          {section.list && (
                            <ul className="list-disc pl-6 space-y-2 mt-4">
                              {section.list.map((item, lIdx) => (
                                <li key={lIdx}>{item}</li>
                              ))}
                            </ul>
                          )}
                          {section.contentAfterList && section.contentAfterList.map((p, pIdx) => (
                            <p key={`after-${pIdx}`} className="mt-4">{p}</p>
                          ))}
                        </div>
                      );
                    })}

                    {/* Contract comparison table */}
                    <div className="mt-8">
                      <h2 className="text-2xl font-bold text-foreground mb-6">{t(`${k}.comparisonTitle`)}</h2>
                      <div className="space-y-4">
                        {contractTypes.map((contract, idx) => (
                          <div key={idx} className="bg-secondary border border-border rounded-sm p-6">
                            <div className="flex items-start gap-4">
                              <FileText className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                              <div>
                                <div className="flex items-center gap-3 mb-1">
                                  <span className="font-bold text-foreground text-lg">{contract.code}</span>
                                  <span className="text-accent text-xs uppercase tracking-wider font-medium">{contract.bestFor}</span>
                                </div>
                                <h3 className="font-semibold text-foreground mb-2">{contract.name}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{contract.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </SectionFadeIn>
              </article>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <SectionFadeIn delay={0.1}>
                <div className="sticky top-32 space-y-6">
                  <div className="bg-foreground rounded-sm p-6 text-primary-foreground">
                    <h3 className="text-lg font-bold mb-3">{t(`${k}.sidebar.cta.title`)}</h3>
                    <p className="text-primary-foreground/70 text-sm mb-4">{t(`${k}.sidebar.cta.description`)}</p>
                    <Button asChild className="w-full bg-brand-yellow text-brand-yellow-foreground hover:bg-brand-yellow/90 uppercase text-xs tracking-wider font-semibold mb-3">
                      <Link to={`${prefix}/contact`}>{t(`${k}.sidebar.cta.buttonText`)}</Link>
                    </Button>
                    <a href="tel:+13476131717" className="flex items-center justify-center gap-2 text-brand-yellow text-sm font-semibold">
                      <Phone className="h-4 w-4" />{t(`${k}.sidebar.cta.phone`)}
                    </a>
                  </div>

                  {/* Quick reference */}
                  <div className="bg-accent/5 border border-accent/20 rounded-sm p-6">
                    <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-accent" />
                      {t(`${k}.sidebar.quickRef.title`)}
                    </h3>
                    <ul className="space-y-3 text-sm">
                      {(t(`${k}.sidebar.quickRef.items`, { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-muted-foreground">
                          <span className="text-accent font-bold mt-0.5">&bull;</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-secondary rounded-sm p-6 border border-border">
                    <h3 className="font-bold text-foreground mb-4">{t(`${k}.sidebar.related.title`)}</h3>
                    <ul className="space-y-3">
                      <li><Link to={`${prefix}/services/construction-consulting`} className="text-sm text-muted-foreground hover:text-accent transition-colors">{sidebarLinks[0]} &rarr;</Link></li>
                      <li><Link to={`${prefix}/blog/resolve-dob-violations`} className="text-sm text-muted-foreground hover:text-accent transition-colors">{sidebarLinks[1]} &rarr;</Link></li>
                      <li><Link to={`${prefix}/blog/facade-inspection-cost-nyc`} className="text-sm text-muted-foreground hover:text-accent transition-colors">{sidebarLinks[2]} &rarr;</Link></li>
                      <li><Link to={`${prefix}/contact`} className="text-sm text-muted-foreground hover:text-accent transition-colors">{sidebarLinks[3]} &rarr;</Link></li>
                    </ul>
                  </div>
                </div>
              </SectionFadeIn>
            </aside>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ConstructionContractsGuide;
